import React from "react";
import { X } from "lucide-react";
import type { Announcement } from "src/lib/announcements";

type AnnouncementToastProps = {
  announcement: Announcement;
  /** Fired by the primary CTA. */
  onPrimary: () => void;
  /** Fired by "Not now" (only rendered when the announcement is snoozable). */
  onSnooze: () => void;
  /** Fired by the close (X) button. */
  onDismiss: () => void;
};

/**
 * A single announcement card. Purely presentational — positioning, animation,
 * stacking, and persistence all live in {@link AnnouncementCenter}.
 */
const AnnouncementToast: React.FC<AnnouncementToastProps> = ({
  announcement,
  onPrimary,
  onSnooze,
  onDismiss,
}) => {
  const { icon: Icon, badge, title, body, primary, snoozeMs } = announcement;
  const titleId = `announcement-${announcement.id}-title`;
  const bodyId = `announcement-${announcement.id}-body`;

  return (
    <div
      role="group"
      aria-labelledby={titleId}
      aria-describedby={bodyId}
      className="relative overflow-hidden rounded-[1.5rem] border-4 border-white/15 bg-gray-950/95 p-4 shadow-[0_10px_0_rgba(0,0,0,0.45)] backdrop-blur-xl"
    >
      <button
        type="button"
        onClick={onDismiss}
        className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-xl border-2 border-white/10 text-gray-400 transition-colors hover:border-white/20 hover:bg-white/10 hover:text-white focus:outline-none focus-visible:ring-4 focus-visible:ring-teal-400/40"
        aria-label="Dismiss"
      >
        <X className="h-4 w-4" aria-hidden />
      </button>

      <div className="flex items-start gap-3 pr-8">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border-2 border-amber-400/40 bg-amber-400/15 text-amber-300 shadow-inner">
          <Icon className="h-5 w-5" aria-hidden />
        </span>
        <div className="min-w-0">
          {badge && (
            <span className="mb-1 inline-block rounded-full bg-amber-400 px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wide text-gray-950">
              {badge}
            </span>
          )}
          <h2 id={titleId} className="text-base font-extrabold text-white">
            {title}
          </h2>
          <p id={bodyId} className="mt-1 text-sm leading-5 text-gray-400">
            {body}
          </p>
        </div>
      </div>

      <div className="mt-4 flex flex-col gap-2 sm:flex-row">
        <button
          type="button"
          onClick={onPrimary}
          className="min-h-11 flex-1 rounded-2xl border-4 border-[#0F766E] bg-[#14B8A6] px-4 text-sm font-extrabold uppercase tracking-wide text-gray-950 shadow-[0_5px_0_#0F766E] transition-transform active:translate-y-1 active:shadow-[0_2px_0_#0F766E] focus:outline-none focus-visible:ring-4 focus-visible:ring-teal-400/40"
        >
          {primary.label}
        </button>
        {snoozeMs != null && (
          <button
            type="button"
            onClick={onSnooze}
            className="min-h-11 rounded-2xl border-4 border-white/10 px-4 text-sm font-extrabold text-gray-300 shadow-[0_4px_0_rgba(0,0,0,0.35)] transition-transform hover:bg-white/5 hover:text-white active:translate-y-0.5 focus:outline-none focus-visible:ring-4 focus-visible:ring-white/20"
          >
            Not now
          </button>
        )}
      </div>
    </div>
  );
};

export default AnnouncementToast;
